// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  mdiCloudDownload,
  mdiCloudUpload,
  mdiContentSavePlus,
  mdiDelete,
  mdiDotsVertical,
  mdiFilterPlus,
  mdiFolderPlus,
  mdiFormTextbox,
  mdiPlusCircle,
  mdiRefresh
} from "@mdi/js";
import { Popconfirm, Popover, Spin, Upload } from "antd";
import { UploadFile } from "antd/lib/upload/interface";
import { IS_INTERNAL_ENV } from "constants/app.constants";
import { TYPE_WELLS, USER_ARPS } from "constants/settings.constants";
import _ from "lodash";
import {
  filterWellsFromList,
  setForecastFolders,
  setHasViewItemsChanged
} from "store/features";
import { RootState } from "store/rootReducer";
import {
  selectExpandedForecastKeys,
  selectExpandedTypeWellKeys,
  selectHasUnsavedChanges,
  selectSyncStateId,
  selectSyncStateType,
  selectTemporaryForecast,
  useForecastStore
} from "stores/forecast";
import styled from "styled-components";

import { ForecastFolder, ForecastFolderItem } from "models/UserArpsModel";

import { UnsavedTypeWellChangesPopconfirm } from "components/arps/UnsavedTypeWellChangesPopconfirm";
import { useValNavFolderTypeWellUpdater } from "components/arps/hooks";
import { Tooltip } from "components/base";
import { IconSpinner, TemplateDownload } from "components/icons";

import { useDeleteForecast } from "../../arps/hooks/useDeleteForecast";
import { useSelectedProject } from "../projects/hooks";
import ExportForecastsButton, {
  WellSource,
  hasExportableForecasts
} from "./ExportForecastsButton";
import { FolderSaveAs } from "./FolderSaveAs";
import ValNavTypeWellImporter from "./ValNavTypeWellImporter";
import { useForecastContext, useForecastDispatch } from "./context";
import { useAddNewTypeWell } from "./hooks";
import { useAddForecastFolderList } from "./hooks/useAddForecastFolderList";
import { useWellDataTemplateDownload } from "./hooks/useWellDataTemplateDownload";
import { getUntitledTypeWellName } from "./utils";

export default function FolderNodeActions({
  fNode,
  type,
  source,
  updateFolderWithValNavData,
  onSaveTypeWell,
  location,
  uploadValNav,
  onAcceptFiles,
  setValnavError
}) {
  const dispatch = useDispatch();
  const forecastDispatch = useForecastDispatch();
  const fsSetHasUnsavedChanges = useForecastStore((s) => s.setHasUnsavedChanges);
  const fsSetIsCreatingTypeWell = useForecastStore((s) => s.setIsCreatingTypeWell);
  const setExpandedTypeWellKeys = useForecastStore((s) => s.setExpandedTypeWellKeys);
  const setExpandedForecastKeys = useForecastStore((s) => s.setExpandedForecastKeys);
  const { addNewTypeWell } = useAddNewTypeWell(type, source);
  const { mutate } = useAddForecastFolderList(type, source);
  const { downloadTemplate } = useWellDataTemplateDownload();

  const { temporaryForecastFolderList, forecastNameField } = useForecastContext();

  const forecastFolders = useSelector(
    (state: RootState) =>
      state.arps[type === TYPE_WELLS ? "typeWellFolders" : "forecastFolders"]
  );

  const expandedTypeWellKeys = useForecastStore(selectExpandedTypeWellKeys);
  const expandedForecastKeys = useForecastStore(selectExpandedForecastKeys);
  const expandedKeys = type === TYPE_WELLS ? expandedTypeWellKeys : expandedForecastKeys;

  const temporaryForecast = useForecastStore(selectTemporaryForecast);

  function handleFolderWellListFilter(folder) {
    const filteredWells = folder.children.map((child) => child.uniqueID);
    if (filteredWells?.length > 0) {
      dispatch(filterWellsFromList({ wellList: filteredWells }));
    }
  }
  const { selectedProject: project } = useSelectedProject();

  const deleteForecast = useDeleteForecast(type);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [pendingAddTypeWell, setPendingAddTypeWell] = useState(false);

  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [isPreventingTypeWellCreation, setIsPreventingTypeWellCreation] = useState(false);

  const { onUpdateValNavFolder: syncFolder, valnavError } =
    useValNavFolderTypeWellUpdater(type);

  useEffect(() => {
    // use the memoized prop
    setValnavError(valnavError);
  }, [valnavError]);

  const syncStateType = useForecastStore(selectSyncStateType);
  const syncStateId = useForecastStore(selectSyncStateId);
  const isSyncingFolders = syncStateType === "folder";
  const syncingFolderId = syncStateType === "folder" ? syncStateId : null;

  // Compute whether this specific folder should show spinner
  const isThisFolderSyncing = isSyncingFolders && syncingFolderId === fNode.folderId;

  const hasUnsavedTypeWellChanges = useForecastStore(selectHasUnsavedChanges);

  const typeWellUploadProps = {
    multiple: false,
    accept: ".csv,.xlsx,.xls,.xml",
    fileList: [], // This is important to clear the files after they are uploaded.
    showUploadList: false,
    beforeUpload: (file: UploadFile) => {
      const files = onAcceptFiles ? onAcceptFiles([file]) : [file];
      uploadValNav(fNode.folderName, fNode.folderId, files);
      return false;
    }
  };

  function newFolder(parentId) {
    const foldersCopy = JSON.parse(JSON.stringify(forecastFolders));
    foldersCopy.forEach((folder) => {
      folder.isEdit = false;
    });
    const folderName = "New Folder";
    const folderItem: ForecastFolderItem = {
      folderId: null,
      projectId: project?.projectId,
      forecasts: null,
      name: folderName,
      folderName: folderName,
      reserveCategory: "",
      parentId: parentId
    };
    const folder = new ForecastFolder(folderItem);
    folder.isEdit = true;
    folder.editLocation = location;
    forecastDispatch({ payload: { forecastNameField: folder.folderName } });

    // Only add to expanded keys if not already expanded
    const updatedExpandedKeys = expandedKeys.includes(parentId)
      ? expandedKeys
      : [...expandedKeys, parentId];

    if (type === TYPE_WELLS) {
      setExpandedTypeWellKeys(updatedExpandedKeys);
    } else {
      setExpandedForecastKeys(updatedExpandedKeys);
    }
    dispatch(
      setForecastFolders({ type: type, forecastFolders: [...foldersCopy, folder] })
    );
  }

  function setFolderToEditable(folder) {
    const copy: ForecastFolder = _.cloneDeep(folder);
    const folders = _.cloneDeep(forecastFolders);
    folders.forEach((folder) => (folder.isEdit = false));
    folders.forEach((folder) =>
      folder.children.forEach((child) => (child.isEdit = false))
    );

    copy.isEdit = true;
    copy.editLocation = source;
    copy.children.forEach((child) => (child.isEdit = false));

    const idx = folders.findIndex((folder) => folder.key === copy.key);
    if (idx >= 0) {
      folders.splice(idx, 1, copy);
    }
    dispatch(setForecastFolders({ type: type, forecastFolders: folders }));
    forecastDispatch({ payload: { forecastNameField: folder.folderName } });
  }
  const collectFoldersToDelete = (node) => {
    const foldersToDelete = [];

    const traverse = (currentNode) => {
      if (currentNode.children && currentNode.children.length > 0) {
        currentNode.children.forEach((child) => {
          foldersToDelete.push({
            folderId: child.folderId,
            id: child.id,
            type: child.type ?? "forecast"
          });
          traverse(child);
        });
      }
    };

    traverse(node);
    return foldersToDelete;
  };

  function deleteFolder() {
    if (!project?.projectId) {
      return;
    }
    const foldersToDelete = collectFoldersToDelete(fNode);

    foldersToDelete.push({
      folderId: fNode.folderId,
      // Backend matches the folder document by its `folderId` field, so send folderId
      // (not fNode.id — USER_ARPS folders carry a `folderId_<rescat>` id that won't match),
      // and tag it as a folder so the backend actually deletes the folder doc, not just its contents.
      id: fNode.folderId,
      type: "folder"
    });

    deleteForecast
      .delete(foldersToDelete)
      .then(async () => {
        forecastDispatch({ payload: { error: null } });
        return;
      })
      .catch((err) => {
        forecastDispatch({ payload: { error: `Error deleting. ${err.message}` } });
      });
    fsSetHasUnsavedChanges(false);
  }

  const handleAddTypeWell = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check for unsaved changes
    if (hasUnsavedTypeWellChanges) {
      setPendingAddTypeWell(true);
      return;
    }

    await performAddTypeWell();
  };

  const performAddTypeWell = async () => {
    if (isPreventingTypeWellCreation) {
      return;
    }

    setIsPreventingTypeWellCreation(true);
    fsSetIsCreatingTypeWell(true);

    try {
      const currentTemporaryForecast = temporaryForecast;
      const currentForecastNameField = forecastNameField;

      let titleToPassToAddNew = currentForecastNameField;

      if (temporaryForecastFolderList.length > 0) {
        await mutate();
      } else if (currentTemporaryForecast) {
        const titleToSave =
          currentForecastNameField?.trim() ||
          currentTemporaryForecast.title ||
          getUntitledTypeWellName(currentTemporaryForecast, forecastFolders);

        titleToPassToAddNew = titleToSave;

        forecastDispatch({ payload: { forecastNameField: titleToSave } });

        const saveResult = await onSaveTypeWell(
          currentTemporaryForecast,
          titleToSave,
          false
        );

        if (!saveResult?.success) {
          forecastDispatch({
            payload: {
              error: "Failed to save previous type well. Please try again."
            }
          });
          return;
        }
      }

      await addNewTypeWell(fNode as ForecastFolder, titleToPassToAddNew);
    } finally {
      setIsPreventingTypeWellCreation(false);
      fsSetIsCreatingTypeWell(false);
    }
  };

  const handleConfirmAddTypeWell = useCallback(async () => {
    setPendingAddTypeWell(false);
    await performAddTypeWell();
  }, [performAddTypeWell]);

  const handleCancelAddTypeWell = useCallback(() => {
    setPendingAddTypeWell(false);
  }, []);

  const TypeWellFolderMoreOptions = () => {
    return (
      <ActionListWrapper>
        <TypeWellAction
          onClick={(evt) => {
            setFolderToEditable(fNode);
            evt.stopPropagation();
            evt.preventDefault();
          }}>
          <Icon path={mdiFormTextbox} size={"18px"} />
          Rename
        </TypeWellAction>
        {type === TYPE_WELLS && (
          <Popover
            placement="right"
            trigger="click"
            open={isSaveAsModalOpen}
            onOpenChange={(isOpen) => {
              setIsSaveAsModalOpen(isOpen);
            }}
            content={
              <FolderSaveAs
                isOpen={isSaveAsModalOpen}
                selectedFolder={fNode as ForecastFolder}
                onCancel={() => setIsSaveAsModalOpen(false)}
              />
            }>
            <TypeWellAction
              onClick={(evt) => {
                evt.stopPropagation();
                evt.preventDefault();
              }}>
              <Icon path={mdiContentSavePlus} size={"18px"} />
              Save As
            </TypeWellAction>
          </Popover>
        )}
        {type === TYPE_WELLS && (
          <TypeWellAction
            onClick={(evt) => {
              dispatch(setHasViewItemsChanged(true));
              newFolder(fNode.folderId);
              evt.stopPropagation();
              evt.preventDefault();
            }}>
            <Icon path={mdiFolderPlus} size={"18px"} />
            Add Folder
          </TypeWellAction>
        )}

        <Popconfirm
          placement="bottom"
          onConfirm={(evt) => {
            deleteFolder();
            evt.stopPropagation();
            evt.preventDefault();
          }}
          onCancel={(evt) => {
            evt.stopPropagation();
            evt.preventDefault();
          }}
          okText="Delete"
          okType="danger"
          title={`Are you sure you want to delete the folder and its contents?`}>
          <TypeWellAction danger={true}>
            <Icon path={mdiDelete} size={"18px"} />
            Delete
          </TypeWellAction>
        </Popconfirm>

        {type === TYPE_WELLS && (
          <TypeWellAction
            onClick={(evt) => {
              downloadTemplate(fNode as ForecastFolder);
              evt.stopPropagation();
              evt.preventDefault();
            }}>
            <TemplateDownload />
            Well Data Template
          </TypeWellAction>
        )}

        {type === TYPE_WELLS && (
          <ExportForecastsButton
            wellSource={WellSource.TypeWells}
            node={fNode}
            type="folder"
          />
        )}

        {type === USER_ARPS && hasExportableForecasts(fNode) && (
          <ExportForecastsButton
            wellSource={WellSource.UserForecasts}
            node={fNode}
            type="folder"
          />
        )}
      </ActionListWrapper>
    );
  };

  return (
    <>
      {isThisFolderSyncing && <SyncSpinner indicator={<IconSpinner />} />}
      <div className="edit-icon">
        <ForecastActionButtonContainer>
          {!isThisFolderSyncing && type === USER_ARPS && fNode.children?.length > 0 && (
            <Tooltip placement="top" title="Filter to Well List">
              <ForecastActionButton
                onClick={(evt) => {
                  handleFolderWellListFilter(fNode);
                  evt.stopPropagation();
                  evt.preventDefault();
                }}>
                <Icon path={mdiFilterPlus} size={"21px"} />
              </ForecastActionButton>
            </Tooltip>
          )}

          {!isThisFolderSyncing && type === TYPE_WELLS && (
            <>
              <Tooltip placement="top" title="Add new type well to folder">
                <UnsavedTypeWellChangesPopconfirm
                  open={pendingAddTypeWell}
                  placement={"bottom"}
                  onConfirm={handleConfirmAddTypeWell}
                  onCancel={handleCancelAddTypeWell}>
                  <ForecastActionButton
                    data-testid="new-tw-button"
                    onClick={handleAddTypeWell}>
                    <Icon path={mdiPlusCircle} size={"21px"} />
                  </ForecastActionButton>
                </UnsavedTypeWellChangesPopconfirm>
              </Tooltip>

              <StyledUpload {...typeWellUploadProps} data-testid="upload-typewell-button">
                <Tooltip placement="top" title="Upload type well to folder">
                  <ForecastActionButton>
                    <Icon path={mdiCloudUpload} size={"23px"} />
                  </ForecastActionButton>
                </Tooltip>
              </StyledUpload>
            </>
          )}

          {!isThisFolderSyncing && type === TYPE_WELLS && IS_INTERNAL_ENV && (
            /*import type well from valnav*/
            <>
              <ValNavTypeWellImporter
                onOk={async (job, rescat, typeWells) => {
                  const ok = await updateFolderWithValNavData(
                    fNode,
                    {
                      jobNumber: job,
                      rescat,
                      typeWells
                    },
                    false,
                    true
                  );
                  if (!ok) {
                    forecastDispatch({ payload: { error: "Unable to update folder." } });
                  }
                }}>
                <Tooltip placement="top" title="Sync client job type well">
                  <ForecastActionButton>
                    <Icon path={mdiCloudDownload} size={"23px"} />
                  </ForecastActionButton>
                </Tooltip>
              </ValNavTypeWellImporter>
              <Tooltip placement="top" title="Sync all type wells in folder">
                <ForecastActionButton
                  data-testid="sync-folder-button"
                  onClick={() => syncFolder(fNode)}>
                  <Icon path={mdiRefresh} size={"21px"} />
                </ForecastActionButton>
              </Tooltip>
            </>
          )}
          {!isThisFolderSyncing && fNode.projectId !== "organization" && (
            <Tooltip className={"visible-on-hover"} title="More">
              <Popover
                arrowPointAtCenter={true}
                onOpenChange={(isOpen) => {
                  setIsPopoverOpen(isOpen);
                }}
                content={TypeWellFolderMoreOptions()}
                open={isPopoverOpen}
                overlayClassName="popover-no-padding"
                placement="bottomLeft"
                trigger="click">
                <ForecastActionButton
                  type={"text"}
                  data-testid="folder-more-options-popover"
                  onClick={(event) => event.stopPropagation()}>
                  <Icon path={mdiDotsVertical} size={"21px"} />
                </ForecastActionButton>
              </Popover>
            </Tooltip>
          )}
        </ForecastActionButtonContainer>
      </div>
    </>
  );
}

// Custom spinner for folder sync with always-visible styling
const SyncSpinner = styled(Spin)`
  margin-left: auto;
  padding: 0 3px;
`;

// This is necessary to align the upload icon with the other folder icons.
const StyledUpload = styled(Upload)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ForecastActionButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionListWrapper = styled.div`
  padding: 6px;
`;

const ForecastActionButton = styled.button`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  margin: 3px;
  cursor: pointer;
  background: transparent;
  color: ${(props) => props.color?.toString() ?? "#a2aaad"};

  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
  }

  &[disabled]:hover > svg {
    cursor: default;
    color: lightgray;
  }
`;

const TypeWellAction = styled.div`
  height: 3.2rem;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-50);
  font-weight: 500;
  padding: 0 var(--space-3);
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &:hover {
    color: ${(props) =>
      props.disabled
        ? "var(--color-text-50)"
        : props.danger
        ? "var(--color-danger)"
        : "var(--color-primary)"};
  }
`;
